/* eslint-disable react/require-default-props */
/* eslint-disable camelcase */
import React, {
  createContext, useContext, useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { initIndexDB } from '@thd-olt-global/thd-storage-utils';
import { useMaaSession } from '../platform/session/session';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { emit, subscribe, unsubscribeAll } from '../platform/events/lifecycle';
import { createSlicedStore, combineSlices, useStore } from '../store/createStore';
import { composerSlice } from '../store/slices/composer';
import { fileUploadSlice } from '../store/slices/fileUpload';
import { suggestionsSlice } from '../store/slices/suggestions';
import { messagesSlice } from '../store/slices/messages';
import { useMessageHistory } from '../hooks/useMessageHistory';
import { LazyAssistantDataLayer } from './dynamic-import';
import { useLiveAgentBridge, liveAgentSlice } from '../liveAgent';
import { useDrawerControl } from '../shell/DrawerControlContext';
import { useFileUpload } from '../hooks/useFileUpload';
import {
  MESSAGE_ORIGIN_UNSPECIFIED,
  IMAGE_UPLOAD_ANALYTICS_EVENT_NAME,
  SUBMIT_ANALYTICS_MAA_PRIMARY_CATEGORY,
} from '../shared/constants';
import { updateConnectingToConnected as updateConnectingToConnectedUtil } from '../shared/utils/message-utils';
import { getDeviceType } from '../shared/utils/common';
import { handleBotResponse } from './flows/botResponse';
import { runAskUserQuestion } from './flows/askUserQuestion';

const indexDBConfig = {
  version: 2,
  stores: {
    sessions: {
      storeConfig: { keyPath: 'sessionId' },
      indexes: [{ name: 'sessionId', unique: true }],
    },
    messages: {
      storeConfig: { keyPath: 'id', autoIncrement: true },
      indexes: [{ name: 'id', unique: true }],
    }
  }
};

initIndexDB('assistantBotDB', indexDBConfig).catch(() => {});

const AssistantContext = createContext({
  store: null,
  historyCleared: false,
  historyLoading: false,
  messages: [],
  askUserQuestion: () => {},
  saveMessage: () => {},
  clearHistory: () => {},
  setHistoryCleared: () => {},
  resetConversation: () => {},
  updateMessage: () => {},
  // Sprinklr live-agent state
  isEscalated: false,
  isAgentConnected: false,
  // File upload state
  isSubmitting: false,
  setIsSubmitting: () => {},
  imageData: null,
  uploadError: null,
  setUploadError: () => {},
  isLoading: false,
  handleImageChange: () => {},
  handleSetFile: () => {},
  clearImage: () => {},
  documentFile: null,
  handleDocumentChange: () => {},
  clearDocument: () => {},
  isDocumentLoading: false,
});

// The conversational flows the provider dispatches. Injected as a prop (sourced from
// the active journey's manifest by the composition root — see AssistantDataProvider)
// rather than read via useJourney(): the provider is mounted ABOVE JourneyProvider in
// the tree (deliberately, to avoid a provider → journeys → renderer → provider import
// cycle — see JourneyProvider), so it cannot consume the journey context and instead
// receives the journey's flows from above. This default is the fallback for isolated
// mounts (e.g. unit tests) and is identical to the eager AssistantJourney.flows.
// NOTE: a lazy journey overriding these provider-level flows would need journey
// resolution hoisted above this provider; today lazy journeys reuse the default flows.
const DEFAULT_FLOWS = { runAskUserQuestion, handleBotResponse };

// The journey's block config, injected the same way and for the same reason as DEFAULT_FLOWS
// (this provider is above JourneyProvider). A journey declares which optional domain blocks
// it opts into; today the only one is `liveAgent`. Isolated mounts (unit tests) get the full
// feature set by default, matching the eager AssistantJourney.config.
const DEFAULT_CONFIG = { liveAgent: true, queryLayer: true };

// Create a provider component
/**
 * @param { AssistantOrchestratorProps } props
 * @returns { React.Provider<AssistantContextProps> }
 */
export const AssistantOrchestrator = ({
  withPersistence = true, withDataLayer = true, flows = DEFAULT_FLOWS, config = DEFAULT_CONFIG,
  children,
}) => {
  // Identity record — read-only here. The record reads persisted storage, so this is the id
  // the Shell seeded, not one this lazily-imported chunk resolved for itself from a cookie
  // read taken whenever it happened to load. That id is what the Sprinklr context and the
  // analytics events are built from, so one page load must only ever produce one.
  const [session] = useMaaSession();
  // Provider-level store (mirrors DrawerProvider). Domain state migrates off the
  // context object into slices here one at a time; components read via selectors
  // (useAssistantStore) so only the selecting component re-renders on a change.
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createSlicedStore(
      combineSlices(
        composerSlice, fileUploadSlice, suggestionsSlice, liveAgentSlice, messagesSlice
      )
    );
  }
  const store = storeRef.current;
  // A RATCHET, not a one-time freeze — grant-late must work. See
  // .claude/context/data-layer-sharing.md.
  const hasQueryLayerRef = useRef(config.queryLayer === true);
  if (config.queryLayer === true) hasQueryLayerRef.current = true;
  const hasQueryLayer = hasQueryLayerRef.current;
  // Reactive state (historyCleared, composer flags, surveyOverlayUrl, …) lives in
  // the store and is read by consumers via useAssistantStore selectors — the
  // provider only needs the (stable) slice actions to drive it.
  const { setHistoryCleared } = store.getState();
  // Internal to the orchestrator since Seq D.5: the cross-tab channel it uses to notify
  // peers of a conversation reset and to hear theirs. It used to be published on the
  // context so descendants could post their own messages onto it — product pods (D.1) and
  // askUserQuestion (D.3) did, both to reach an ANCESTOR. With those gone nothing outside
  // reads it, so it stops being part of the assistant's public surface.
  const maaBroadcastChannel = useRef(null);
  const { setIsSubmitting, setUploadError } = store.getState();

  const { hosts, channel, isConsumerApp } = useContext(ExperienceContext);
  // MAG4-856: the surface an escalation comes from, sent on the Sprinklr conversation
  // context. Derived HERE rather than inside the liveAgent block: the root owns the
  // experience, and a block that read ExperienceContext for itself would be reaching
  // upward through the tree for something its composer already knows.
  const deviceType = useMemo(
    () => getDeviceType({ channel, isConsumerApp }),
    [channel, isConsumerApp],
  );
  // Drawer control from App (Seq D.3). Passed into the flow ctx so askUserQuestion can open
  // the drawer with a direct downward call instead of a self-addressed cross-tab broadcast.
  const { openDrawer } = useDrawerControl();

  // ------ Query layer (both GraphQL operations) ------
  // Mounted as a BLOCK below, not called as hooks here: `useLazyDataModel` cannot be de-hooked
  // (it resolves the GraphQL document from the DataModel schema DSL through QueryContext), so
  // the gate is "don't render it" rather than "don't construct it". A journey that omits
  // `queryLayer` therefore issues neither operation.
  //
  // The block hands its triggers to `registerQueryLayer`; everything downstream gets a stable
  // wrapper, so adding or removing the block never changes the flow ctx's identity — and
  // message history can ask for suggested questions without importing the query layer, which
  // is the indirection F.d had to break (core used to reach it through useGenerateQuestions).
  // ⚠ The dispatchers QUEUE when the block is not mounted yet, and the queue drains the moment
  // it registers. This is load-bearing, not defensive: the host page can fire
  // `user-ask-question` while the drawer is CLOSED, and that flow calls openDrawer() and
  // dispatches to the bot in the same turn — so the request is made one render before the data
  // layer exists. Without the queue it would vanish with no error and no bot reply.
  const queryCapabilities = useRef({});
  const queuedCalls = useRef([]);
  const dispatchToQueryLayer = useCallback((name, arg) => {
    const capability = queryCapabilities.current[name];
    if (capability) return capability(arg);
    queuedCalls.current.push([name, arg]);
    return undefined;
  }, []);
  const registerQueryLayer = useCallback((capabilities) => {
    queryCapabilities.current = capabilities;
    const queued = queuedCalls.current;
    queuedCalls.current = [];
    queued.forEach(([name, arg]) => capabilities[name]?.(arg));
  }, []);
  const submitUserQuestionToApi = useCallback(
    (input) => dispatchToQueryLayer('submitUserQuestion', input), [dispatchToQueryLayer],
  );
  const submitGenerateQuestions = useCallback(
    (params) => dispatchToQueryLayer('submitGenerateQuestions', params), [dispatchToQueryLayer],
  );

  const {
    messages,
    lastBotMessage,
    lastUserMessage,
    saveMessage,
    saveMessageIfNew,
    clearHistory,
    updateMessage,
    broadCastMessageUpdate,
    iDBGetAll_messages,
    iDBUpdate_message,
    loadIDBMessages,
    commitSuggestedQuestions,
  } = useMessageHistory(withPersistence, session, store, submitGenerateQuestions);

  // useMessageHistory now publishes its message-history + ISQ state (messages,
  // last*Message, historyLoading, isSuggestedQuestionsLoading, wereQuestionsGenerated)
  // into the store itself — no provider effect-bridges (WS-10.1). The values above are
  // still destructured because the provider's own callbacks (onSurveyReceived,
  // onImageReceived, askUserQuestion ctx, the bot-response effect) read them directly.

  const updateConnectingToConnected = useCallback(
    () => updateConnectingToConnectedUtil({
      getAll: iDBGetAll_messages,
      update: iDBUpdate_message,
      reload: loadIDBMessages,
    }),
    [iDBGetAll_messages, iDBUpdate_message, loadIDBMessages],
  );

  // ------ LiveAgent domain module ------
  // The whole escalated-conversation domain (Sprinklr transport + escalation state machine
  // + idle TTL + reconnect + cross-tab sync + the post-chat survey lifecycle + its analytics)
  // lives in src/liveAgent. The orchestrator's only job here is to hand it the Conversation
  // port it writes through and the store it publishes into (Seq C.2) — the six
  // Sprinklr→Conversation callbacks that used to sit in this file are the module's own.
  // The module publishes escalation + the agent-connection flags into the store itself, so
  // there are no provider effect-bridges, and `isAgentConnected` never crosses the boundary —
  // the flows ask only about intent and the block reconciles (Seq F.c).
  //
  // What this root imports is a BRIDGE, not the block: the block is a factory, so a journey
  // that omits it constructs nothing and the bridge hands back an inert surface. Membership
  // is a ratchet — see useLiveAgentBridge.js / .claude/context/live-agent.md.
  const {
    checkIdleExpired,
    initiateAgentChat,
    sendAgentMessage,
    sendAgentImage,
    updateLastActivity,
    handleIdleDisconnect,
    openSurveyOverlay,
    closeSurveyOverlay,
    onSurveyDismiss,
  } = useLiveAgentBridge({
    // `=== true`, not `!== false`: blocks are OPT-IN, so a journey that says nothing about
    // liveAgent does not get it. The gate read `!== false` until Seq F.d, which made the
    // contract's own documentation false — `paintColorPicker` was told it HAD to declare the
    // block and did not, and the F.c.0 opt-in test asserted on a mock's own expression rather
    // than on this one. Both real journeys declare it explicitly, so this is a no-op today.
    enabled: config.liveAgent === true,
    store,
    deviceType,
    conversation: {
      messages,
      saveMessage,
      saveMessageIfNew,
      updateMessage,
      broadCastMessageUpdate,
      updateConnectingToConnected,
    },
  });

  const {
    handleImageChange,
    handleSetFile,
    clearImage,
    handleDocumentChange,
    clearDocument,
  } = useFileUpload(setUploadError, store);

  // useFileUpload and the query-layer block publish their own state (upload fields,
  // botLoading) into the store directly — no provider effect-bridges. Since Seq F.d the
  // bot response is not read here at all: the block calls onBotTurnSettled below.

  // --- Hook Dependent State ---
  const sessionIDRef = useRef(null);
  const actionIDRef = useRef(null);
  const imageMetaRef = useRef(null);
  const messageHistoryRef = useRef(null);

  const sendImageUploadAnalytics = (sessionID, actionID, outcome, userQuestion) => {
    const imageMeta = imageMetaRef.current;
    if (!imageMeta) return;
    const hasText = userQuestion?.trim() ? 'yes' : 'no';
    const fileSizeKb = Number.isFinite(imageMeta.fileSize)
      ? Math.round(imageMeta.fileSize / 1024)
      : 0;
    const searchString = `maa:${imageMeta.imageOrigin} upload:${imageMeta.imageFileType}`
      + ` ${fileSizeKb}kb:text ${hasText}:${outcome}`;
    const analyticsObject = {
      primaryCategory: SUBMIT_ANALYTICS_MAA_PRIMARY_CATEGORY,
      eventName: IMAGE_UPLOAD_ANALYTICS_EVENT_NAME,
      maSessionID: sessionID,
      maActionID: actionID,
      maaSearch: searchString,
    };
    emit('magic-apron.image-submit', analyticsObject);
  };

  useEffect(() => {
    // Re-broadcast the host page's config-service change onto our own lifeCycle bus.
    // The adapter guards the (cross-frame) parent bus + nested lifeCycle on both
    // subscribe and teardown, so an absent/non-standard bus can't throw during unmount.
    const unsubscribePropagate = subscribe('propagate-maa-llm', ({ output }) => {
      emit('CONFIG_SERVICE_CHANGE', output, { channel: 'lifeCycle' });
    }, { frame: 'parent', channel: 'lifeCycle' });

    if (typeof BroadcastChannel !== 'undefined') {
      maaBroadcastChannel.current = createBroadcastChannel(CHANNELS.assistant);
    }
    return () => {
      unsubscribePropagate();
      if (maaBroadcastChannel.current) {
        maaBroadcastChannel.current.close();
      }
    };
  }, []);

  // --- Conversation reset ---
  // Clearing the conversation is a two-audience action: this tab, and every other tab the
  // shopper has open. Seq D.4 split those. `applyReset` is the local effect; the peer
  // notification stays on the BroadcastChannel (genuinely cross-tab — KEEP) but is now
  // emitted BY the orchestrator, which owns the conversation, instead of by the header
  // reaching for a channel to shout at its own ancestor.
  const applyReset = useCallback(() => {
    clearHistory();
    setHistoryCleared(true);
  }, [clearHistory, setHistoryCleared]);

  // Called by the header's "Clear conversation" menu item via useAssistant(). Resets THIS
  // tab directly and notifies peers. Posting from our OWN channel object is what keeps the
  // two paths from colliding: the HTML spec's broadcast algorithm removes the sending
  // object from the destination list, so the listener below cannot fire for our own post
  // and double-reset. Peer orchestrators still receive it.
  const resetConversation = useCallback(() => {
    applyReset();
    if (maaBroadcastChannel.current) {
      maaBroadcastChannel.current.postMessage({ type: 'MAA_RESET' });
    }
  }, [applyReset]);

  useEffect(() => {
    if (maaBroadcastChannel.current) {
      // Peer-originated resets only (see above). The old handler also destructured
      // MessageEvent.origin and passed it to clearHistory, which takes no parameters and
      // silently discarded it — dead plumbing, dropped.
      // MAA_RESET only. Do NOT re-announce readiness on MAA_OPEN_DRAWER — asking a question
      // causes that message, and it loops. See .claude/context/event-bus.md
      maaBroadcastChannel.current.onmessage = ({ data: { type } }) => {
        if (type === 'MAA_RESET') applyReset();
      };
    }
  }, [applyReset, maaBroadcastChannel]);

  // Reconnect-on-reload is the live-agent block's own business: its `init()` resumes the
  // conversation when the persisted record has isEscalated + a conversationId, and a send
  // arriving before that completes reconciles through `ensureConnected` rather than through a
  // branch in a flow (Seq F.c).

  const askUserQuestion = useCallback((input) => flows.runAskUserQuestion(input, {
    session,
    hosts,
    imageData: store.getState().imageData,
    // Flows ask only about INTENT. Whether a connection is live is the live-agent block's
    // business — its sends reconcile before delivering — so `isAgentConnected` no longer
    // crosses this boundary at all.
    getIsEscalated: () => store.getState().isEscalated,
    sessionIDRef,
    actionIDRef,
    imageMetaRef,
    messageHistoryRef,
    openDrawer,
    messages,
    setIsSubmitting,
    setUploadError,
    setHistoryCleared,
    saveMessage,
    submitUserQuestionToApi,
    sendAgentMessage,
    sendAgentImage,
    initiateAgentChat,
    updateLastActivity,
    handleIdleDisconnect,
    checkIdleExpired,
    clearImage,
    broadCastMessageUpdate,
    sendImageUploadAnalytics,
  }), [
    session,
    clearHistory,
    submitUserQuestionToApi,
    saveMessage,
    broadCastMessageUpdate,
    checkIdleExpired,
    initiateAgentChat,
    sendAgentMessage,
    updateConnectingToConnected,
    flows.runAskUserQuestion
  ]);

  // Host page asks a question on our behalf (cross-frame). Handler via ref so the effect below
  // can be mount-only, the same way onBotTurnSettled reads its handlers.
  const askUserQuestionRef = useRef(askUserQuestion);
  askUserQuestionRef.current = askUserQuestion;

  useEffect(() => {
    // ⚠ Mount-only, and tear down with unsubscribeAll: the lifeCycle channel replays, and
    // this handler renders. See .claude/context/event-bus.md
    subscribe('magic-apron-assistant.user-ask-question', ({ output = {} }) => {
      const {
        question,
        messageOrigin = MESSAGE_ORIGIN_UNSPECIFIED,
        entryLocationForAnalytics,
        pageType,
        position
      } = output;
      if (question) {
        askUserQuestionRef.current({
          userQuestion: question,
          messageOrigin,
          suggestedQuestionPosition: position,
          entryLocationForAnalytics,
          pageType,
          isInitialSuggestedQuestion: true,
        });
      }
    }, { frame: 'parent', channel: 'lifeCycle' });

    return () => unsubscribeAll(
      'magic-apron-assistant.user-ask-question',
      { frame: 'parent', channel: 'lifeCycle' },
    );
  }, []);

  // A bot turn resolved. The block calls this instead of the root watching a loading flag:
  // a settled response is an event, and the trigger returned by useLazyQuery cannot be
  // promise-chained, which is the whole reason this was ever an effect. Recreated every
  // render on purpose — the block reads it through a ref at fire time, so the flow always
  // runs against the latest handlers.
  const onBotTurnSettled = ({ data: botResponse, error: botError }) => {
    flows.handleBotResponse({
      botResponse,
      botError,
      lastUserMessage,
      sessionIDRef,
      actionIDRef,
      saveMessage,
      setIsSubmitting,
      broadCastMessageUpdate,
      initiateAgentChat,
      updateConnectingToConnected,
      sendImageUploadAnalytics,
      clearActionId: () => { actionIDRef.current = null; },
    });
  };

  // All reactive state now lives in the store and is read via useAssistantStore
  // selectors; the context object exposes only actions/handlers, the stable refs,
  // the session record, and the store itself.
  const context = {
    store,
    // Actions / handlers
    askUserQuestion,
    saveMessage,
    updateMessage,
    setIsSubmitting,
    setUploadError,
    handleImageChange,
    clearImage,
    handleDocumentChange,
    clearDocument,
    resetConversation,
    // Exposed so the Shell can retire the "New chat started" notice when the drawer session
    // ends. The flag is otherwise sticky: applyReset sets it and only the ask flow clears it.
    setHistoryCleared,
    onSurveyDismiss,
    openSurveyOverlay,
    closeSurveyOverlay,
    // Stable refs
    messageHistoryRef,
    // Session record (single source from useMaaSession)
    session,
  };

  // GraphQL boundary sits BELOW this root, dynamically imported — see
  // .claude/context/data-layer-sharing.md.
  const queryLayer = hasQueryLayer ? {
    session,
    store,
    register: registerQueryLayer,
    onSettled: onBotTurnSettled,
    onQuestionsSettled: commitSuggestedQuestions,
  } : null;

  // Every journey shares one data layer now; `hasQueryLayer` gates only `queryLayer` above.
  // See .claude/context/data-layer-sharing.md.
  const wantsDataLayer = withDataLayer;

  return (
    <AssistantContext.Provider value={context}>
      {wantsDataLayer ? (
        <LazyAssistantDataLayer queryLayer={queryLayer}>
          {children}
        </LazyAssistantDataLayer>
      ) : children}
    </AssistantContext.Provider>
  );
};

AssistantOrchestrator.propTypes = {
  children: PropTypes.node,
  withPersistence: PropTypes.bool,
  // App's latch: false until the drawer has opened once, then true forever (Seq G.3).
  withDataLayer: PropTypes.bool,
  // The active journey's flows, injected from above (see DEFAULT_FLOWS).
  flows: PropTypes.shape({
    runAskUserQuestion: PropTypes.func,
    handleBotResponse: PropTypes.func,
  }),
  // The active journey's block config, injected from above (see DEFAULT_CONFIG).
  config: PropTypes.shape({
    liveAgent: PropTypes.bool,
    queryLayer: PropTypes.bool,
  }),
};

// Custom hook to use the AssistantContext
/**
 * @returns { AssistantContextProps }
 */
export const useAssistant = () => {
  return useContext(AssistantContext);
};

// Reactive selector into the assistant store. Only components that call this
// re-render when their selected slice changes (mirrors useDrawerStore). State
// migrates off the context object into store slices one domain at a time. Pass
// `shallow` as isEqual to select several fields in one object without re-rendering
// on every store update.
export const useAssistantStore = (selector, isEqual) => {
  const { store } = useContext(AssistantContext);
  return useStore(store, selector, isEqual);
};
